'use client';

import type { CSSProperties } from 'react';

import { useIsAppUser } from '@ps/functions/hooks/useIsAppUser';
import { cn } from '@ps/ui/utils';

import type { MessageMeContext } from '../../message-creator/MessageMeButton';
import { MessageMeButton } from '../../message-creator/MessageMeButton';
import type { MainActionRowViewModel } from '../types/MainActionRowViewModel';
import { MainActionLink } from './MainActionLink';
import { SubscribeLabel } from './SubscribeLabel';

/**
 * Buttons hidden inside the CommentSold native app for App-Store IAP
 * compliance — subscribe and tip both take money via web checkout.
 */
const kAppUserHiddenTypes = new Set(['subscribe', 'tip']);

interface Props {
  rows: MainActionRowViewModel[];
  backgroundColor: string;
  textColor: string;
  textStyle: CSSProperties;
  /** Creator identity + DM gate inputs for the `dm` (Message Me) button island. */
  dm: MessageMeContext;
  className?: string;
}

/**
 * Visitor main action buttons — renders the rows view model as a column of
 * rows (`gap`), each row a flex row (`gap`) of `flex-1` buttons. Every button is
 * a plain server-rendered `MainActionLink` EXCEPT `dm` (Message Me), which is a
 * client island gating on login/subscription before it navigates. Pure /
 * server-renderable otherwise, font-config-context-free; renders tight (host
 * owns inter-section spacing).
 */
export function MainActionsRows({
  rows,
  backgroundColor,
  textColor,
  textStyle,
  dm,
  className,
}: Props) {
  const isAppUser = useIsAppUser();
  const visibleRows = isAppUser
    ? rows
        .map(row => ({
          ...row,
          buttons: row.buttons.filter(b => !kAppUserHiddenTypes.has(b.type)),
        }))
        .filter(row => row.buttons.length > 0)
    : rows;
  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      {visibleRows.map((row, rowIndex) => (
        <div
          // Rows are positional + stable; index key is fine.
          key={rowIndex}
          className="flex w-full flex-row gap-2"
        >
          {row.buttons.map(button =>
            button.type === 'dm' ? (
              // Message Me is a client island — it gates on login/subscription
              // before navigating to the chat.
              <MessageMeButton
                key={button.type}
                button={button}
                context={dm}
                backgroundColor={backgroundColor}
                textColor={textColor}
                textStyle={textStyle}
              />
            ) : (
              <MainActionLink
                key={button.type}
                button={
                  // The subscribe button's label is follow-aware ("Subscribed"
                  // once the viewer follows) — a client concern, so inject a
                  // client label node. Community keeps its static string and
                  // stays a pure server link (no client JS).
                  button.type === 'subscribe'
                    ? {
                        ...button,
                        labelNode: <SubscribeLabel label={button.label} />,
                      }
                    : button
                }
                backgroundColor={backgroundColor}
                textColor={textColor}
                textStyle={textStyle}
              />
            ),
          )}
        </div>
      ))}
    </div>
  );
}
